import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Send, Lightbulb, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const ChatSupport = ({ user }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ai',
      text: 'Hallo! Ich bin Ihr KI-Assistent für Easyclaim24. Wie kann ich Ihnen bei der Nutzung der Software helfen?',
      timestamp: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: inputMessage,
      timestamp: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages([...messages, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call AI API
      const response = await fetch(getBackendUrl() + '/api/chat/message', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: currentMessage,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store session ID for conversation continuity
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
        }

        const aiResponse = {
          id: messages.length + 2,
          sender: 'ai',
          text: data.response,
          timestamp: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages((prev) => [...prev, aiResponse]);
      } else {
        // Fallback response on error
        const errorResponse = {
          id: messages.length + 2,
          sender: 'ai',
          text: 'Entschuldigung, es gab einen Fehler bei der Verarbeitung Ihrer Nachricht. Bitte versuchen Sie es erneut.',
          timestamp: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorResponse = {
        id: messages.length + 2,
        sender: 'ai',
        text: 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        timestamp: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Chat Support</h1>
          <p className="text-sm text-gray-600 mt-1">Live-Chat mit unserem Support-Team</p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: '#E1E4E8', height: '600px', display: 'flex', flexDirection: 'column' }}>
          {/* Status */}
          <div className="px-6 py-4 border-b" style={{ borderColor: '#E1E4E8' }}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
              <span className="text-sm font-medium text-gray-700">Support Online</span>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg p-4 ${message.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'}`}
                >
                  <p className="text-sm">{message.text}</p>
                  <p className={`text-xs mt-2 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t p-4" style={{ borderColor: '#E1E4E8' }}>
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
              <Lightbulb className="w-4 h-4" />
              <span>Fragen Sie mich zu Funktionen, Arbeitsschritten, Kundenverwaltung und mehr!</span>
            </div>
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Frage zur Software stellen..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                data-testid="chat-input"
              />
              <button
                type="submit"
                className="px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: '#3B82F6' }}
                data-testid="send-message-button"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ChatSupport;
